import { ExclamationCircleIcon, Loader, ProviderConsumer } from '@fluentui/react-northstar';
import { themeNames } from '@fluentui/react-teams';
import {
  AtomicBlockUtils,
  ContentBlock,
  ContentState,
  RichUtils,
  EditorState,
  convertFromRaw,
  convertToRaw
} from 'draft-js';
import React from 'react';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { AppStateContext } from '../../../AppState';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import i18next from 'i18next';
import { catalogImageResolver, discardCatalogImages, prepareCatalogImage, uploadCatalogImage } from './CatalogImage';
import { changeToReport } from './changeReport';
import { toolbarIcons } from './toolbarIcons';
import { MAX_CATALOG_IMAGE_BYTES, catalogImageId, catalogImageProblem, errorMessage, fileNameForType, isLocalImageSrc, parseDataUrl } from './catalogImages';
import { PictureBlockProps, ResizableImage } from './ResizableImage';
import { hasPastedStyles, isDraftEditorHtml, pasteHtmlWithPictures, withPicturesLifted } from './inlinePictures';
import { CatalogImagePopup, LibraryPopupProps } from './CatalogImagePopup';
import { editorContent, editorHtml } from './htmlPictures';
import { withOldGapsAsEmptyLines } from './oldGaps';
import { unformatFirstLine } from './firstLineBackspace';
import { removePicture, selectPicture } from './pictureKeys';
import { Block, DEFAULT_IMAGE_ALIGNMENT, RawData, StyleRange, answerToMarkdown, answerToRaw } from './answerMarkdown';
import './Editor.css';

interface Mention {
  separator?: string;
  trigger: string;
  suggestions: {
    text: string;
    value: string;
    url?: string;
  }[];
}

interface Props {
  isFocus?: boolean;
  markdownString: string;
  onChange?: (interaction: any) => void;
  maxLength?: number;
  position?: 'bottom' | 'top';
  noImage?: boolean;
  readOnly?: boolean;
  useBorder?: boolean;
  height?: string;
  /** Lets pasted, dropped and picked pictures be uploaded. Only for editors - the server refuses anyone else. */
  allowImageUpload?: boolean;
  /**
   * Told the id of every image this editor uploads, so the page can hand back the ones its save did not keep, or
   * all of them when it is left unsaved. The editor itself cannot: it unmounts whenever its tab is switched.
   */
  onImageUploaded?: (id: string) => void;
  /**
   * Told when pictures start and stop uploading, so the page can hold Save meanwhile: until a picture has uploaded
   * it has nothing to be saved as, so a save in that window left it out.
   */
  onUploadingChange?: (uploading: boolean) => void;
  /** Start as tall as the window allows below the editor, rather than a fixed height. */
  growToFit?: boolean;
  /**
   * Line spacing as the web shows it: one Enter is the next line with no gap, and an empty line typed for
   * spacing is kept - Markdown would otherwise collapse it. See blankLines.ts.
   */
  keepBlankLines?: boolean;
}

export interface HtmlProps {
  htmlString: string;
  /** contentEdited: the text has differed from what the editor opened with - not just focus or selection - at some point. */
  onChange?: (interaction: any, contentEdited?: boolean) => void;
  toolbarOptions?: string[];
  toolbarInline?: any;
  mention?: Mention;
  wrapperClassName?: string;
  editorClassName?: string;
  oneLine?: boolean;
  readOnly?: boolean;
  handlePastedText?: (text: string, html: string, edstate: any) => void;
  spellCheck?: boolean;
  /**
   * Whether the message is sent with lines as typed (bug 31856). When it turns on - the first edit of any message
   * sharing the flag - the editor writes the old spacing's gaps in as empty lines and reports the result (#35478).
   */
  linesAsTyped?: boolean;
}

interface State {
  editorState: any;
  imageError?: string;
  uploadsInFlight?: number;
  fitHeight?: number;
}

const IMAGE_ERROR_MS = 3000;

// The word and whole-line forms join a line onto a picture the same way at the line's edge.
const PICTURE_KEYS: Record<string, 'backspace' | 'delete'> = {
  'backspace': 'backspace',
  'backspace-word': 'backspace',
  'backspace-to-start-of-line': 'backspace',
  'delete': 'delete',
  'delete-word': 'delete'
};


export const insertString = (
  currentString: string,
  index: number,
  toInsert: string
) => {
  if (index > 0) {
    return (
      currentString.substring(0, index) + toInsert + currentString.substr(index)
    );
  }

  return toInsert + currentString;
};

export const RawDataToMarkdown = (rawdata: RawData) => {
  let md = '';
  rawdata.blocks.forEach((block: Block) => {
    let textblock = block.text;
    let addedChar = 0;
    block.inlineStyleRanges.forEach((styleRange: StyleRange) => {
      if (styleRange.style === 'BOLD') {
        textblock = insertString(
          textblock,
          styleRange.length + styleRange.offset,
          '**'
        );
        textblock = insertString(textblock, styleRange.offset, '**');
      }
    });
    md = textblock + '\n';
  });

  return md;
};

// Every label the library would otherwise show in its own English: tooltips, the link and picture pop-ups, the
// Block Type placeholder. Both editors use them; the keys were first written for the catalog editor.
const editorLabels = () => ({
  'generic.add': i18next.t('knowledge.add-knowledge-modal.button.add'),
  'generic.cancel': i18next.t('knowledge.add-knowledge-modal.button.cancel'),
  'components.controls.blocktype.blocktype': i18next.t('knowledge.add-knowledge-modal.editor-block-type'),
  'components.controls.inline.bold': i18next.t('knowledge.add-knowledge-modal.editor-bold'),
  'components.controls.inline.italic': i18next.t('knowledge.add-knowledge-modal.editor-italic'),
  'components.controls.inline.strikethrough': i18next.t('knowledge.add-knowledge-modal.editor-strikethrough'),
  'components.controls.list.unordered': i18next.t('knowledge.add-knowledge-modal.editor-bulleted-list'),
  'components.controls.list.ordered': i18next.t('knowledge.add-knowledge-modal.editor-numbered-list'),
  'components.controls.list.indent': i18next.t('knowledge.add-knowledge-modal.editor-indent'),
  'components.controls.list.outdent': i18next.t('knowledge.add-knowledge-modal.editor-outdent'),
  'components.controls.link.link': i18next.t('knowledge.add-knowledge-modal.editor-link'),
  'components.controls.link.unlink': i18next.t('knowledge.add-knowledge-modal.editor-unlink'),
  'components.controls.link.linkTitle': i18next.t('knowledge.add-knowledge-modal.editor-link-title'),
  'components.controls.link.linkTarget': i18next.t('knowledge.add-knowledge-modal.editor-link-target'),
  'components.controls.link.linkTargetOption': i18next.t('knowledge.add-knowledge-modal.editor-link-new-window'),
  'components.controls.emoji.emoji': i18next.t('knowledge.add-knowledge-modal.editor-emoji'),
  'components.controls.remove.remove': i18next.t('knowledge.add-knowledge-modal.clear-formatting'),
  'components.controls.history.undo': i18next.t('knowledge.add-knowledge-modal.editor-undo'),
  'components.controls.history.redo': i18next.t('knowledge.add-knowledge-modal.editor-redo'),
  'components.controls.image.image': i18next.t('knowledge.add-knowledge-modal.image-button'),
  'components.controls.image.fileUpload': i18next.t('knowledge.add-knowledge-modal.image-tab-upload'),
  'components.controls.image.byURL': i18next.t('knowledge.add-knowledge-modal.image-tab-link'),
  'components.controls.image.dropFileText': i18next.t('knowledge.add-knowledge-modal.image-drop'),
  'components.controls.image.enterlink': i18next.t('knowledge.add-knowledge-modal.image-link-placeholder'),
  'components.controls.blocktype.normal': i18next.t('knowledge.add-knowledge-modal.block-type-default'),
  'components.controls.blocktype.h1': i18next.t('knowledge.add-knowledge-modal.block-type-heading-1'),
  'components.controls.blocktype.h2': i18next.t('knowledge.add-knowledge-modal.block-type-heading-2'),
  'components.controls.blocktype.blockquote': i18next.t('knowledge.add-knowledge-modal.block-type-quote'),
  'components.controls.blocktype.code': i18next.t('knowledge.add-knowledge-modal.block-type-code')
});

// The comment editor's icons in place of the library's, for the controls the two editors share.
const inlineIcons = {
  bold: { icon: toolbarIcons.bold },
  italic: { icon: toolbarIcons.italic },
  strikethrough: { icon: toolbarIcons.strikethrough }
};
const controlIcons = () => ({
  list: {
    unordered: { icon: toolbarIcons.unordered },
    ordered: { icon: toolbarIcons.ordered },
    indent: { icon: toolbarIcons.indent },
    outdent: { icon: toolbarIcons.outdent }
  },
  link: {
    link: { icon: toolbarIcons.link },
    unlink: { icon: toolbarIcons.unlink }
  },
  emoji: { icon: toolbarIcons.emoji },
  history: {
    undo: { icon: toolbarIcons.undo },
    redo: { icon: toolbarIcons.redo }
  },
  // The library's tooltip is "Remove"; it clears bold, italic and the like from the selection.
  remove: {
    icon: toolbarIcons.clearFormatting,
    title: i18next.t('knowledge.add-knowledge-modal.clear-formatting')
  }
});

export class EditorComponent extends React.Component<Props, State> {
  maxLength: number;
  // A picture added through the toolbar's upload tab is inserted by the library with only the URL we
  // hand it, so its marker is looked up here by that URL. Pictures pasted as HTML `data:` images
  // land here too once uploaded.
  markerBySrc = new Map<string, string>();
  uploading = new Set<string>();
  // The draft-js editor inside react-draft-wysiwyg, as its editorRef hands it over.
  draftEditor: { focus: () => void; getEditorKey?: () => string; props?: { blockRenderMap?: unknown } } | null = null;
  unmounted = false;
  
  constructor(props) {
    super(props);
    const markdownString = this.props.markdownString;
    this.maxLength = this.props.maxLength || 25000;
    
    const newRawData = answerToRaw(markdownString, !!this.props.keepBlankLines);

    const contentState = convertFromRaw(newRawData);
    const newEditorState = EditorState.createWithContent(contentState);
    this.state = {
      editorState: newEditorState
    };
    this.loadedMarkdown = markdownString;
    this.loadedAsSaved = this.toMarkdown(newEditorState);
    this.lastReported = this.loadedAsSaved;
  }

  /**
   * The answer as it was loaded, and as this editor would save that same content. They differ when the stored
   * text is not in the form this editor writes - trailing blank lines, or anything saved before a formatting
   * change - and clicking into the editor makes it report its content, so comparing against the stored text
   * alone made the page think an untouched answer had been edited and light up Save.
   */
  loadedMarkdown: string;
  loadedAsSaved: string;
  lastReported: string;


  componentDidMount() {
    this.showStoredPictures();
    if (this.props.growToFit) requestAnimationFrame(this.fitToWindow);
  }

  wrapper: HTMLDivElement | null = null;

  /** Measured once, on mount: from the editor's top to the bottom of the window, less a little breathing room. */
  fitToWindow = () => {
    const editor = this.wrapper?.querySelector('.rdw-editor-main');
    if (!editor || this.unmounted) return;
    const fitHeight = Math.max(240, Math.floor(window.innerHeight - editor.getBoundingClientRect().top - 32));
    this.setState({ fitHeight });
  };

  componentDidUpdate(_prevProps: Props, prevState: State) {
    const uploading = !!this.state.uploadsInFlight;
    if (uploading !== !!prevState.uploadsInFlight) this.props.onUploadingChange?.(uploading);
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearTimeout(this.imageErrorTimer);
    // An upload still running when the editor goes is handed back when it lands (reportUpload), so it no longer holds Save.
    if (this.state.uploadsInFlight) this.props.onUploadingChange?.(false);
  }

  imageErrorTimer: ReturnType<typeof setTimeout> | undefined;

  /**
   * A stored answer refers to its pictures by marker, which the browser cannot load. Swap each for
   * a fetched object URL once, keeping the marker for saving. Rebuilt from the current content so
   * anything typed in the meantime survives.
   */
  showStoredPictures = async () => {
    const raw: RawData = convertToRaw(this.state.editorState.getCurrentContent());
    const pending = Object.values(raw.entityMap).filter(e => e.type === 'IMAGE' && e.data?.marker && e.data.src === e.data.marker);
    if (pending.length === 0) return;

    const urls = new Map<string, string>();
    const missing = new Set<string>();
    await Promise.all(pending.map(async e => {
      try { urls.set(e.data.marker, await catalogImageResolver.resolve(catalogImageId(e.data.marker))); } catch { missing.add(e.data.marker); }
    }));
    if (this.unmounted || (urls.size === 0 && missing.size === 0)) return;

    // One that could not be fetched - deleted, or from another environment - is marked so the editor draws it as a
    // picture that cannot be loaded, which can be taken out, rather than shimmering for good. The mark is never saved.
    const current: RawData = convertToRaw(this.state.editorState.getCurrentContent());
    Object.values(current.entityMap).forEach(e => {
      if (e.type !== 'IMAGE' || !e.data?.marker) return;
      if (urls.has(e.data.marker)) e.data.src = urls.get(e.data.marker);
      else if (missing.has(e.data.marker)) e.data.broken = true;
    });
    this.setState({ editorState: EditorState.createWithContent(convertFromRaw(current)) });
  };

  /**
   * Draft-js key commands. Passing one replaces react-draft-wysiwyg's own, whose only job is the toolbar's inline
   * styles from the keyboard (Ctrl+B, Ctrl+I), so that is done here too.
   */
  handleKeyCommand = (command: string, editorState: EditorState) => {
    let next: EditorState | null = null;
    if (command === 'backspace')
      next = unformatFirstLine(editorState);
    else if (['bold', 'italic', 'strikethrough'].includes(command))
      next = RichUtils.handleKeyCommand(editorState, command);
    // Left to draft-js, a key beside a picture joins the line onto it and leaves the cursor inside the picture, which
    // react-draft-wysiwyg throws away while draft-js carries on from it - Backspace took three presses - and Delete
    // pulled the picture into the line for withPicturesLifted to put straight back below the cursor (#35487).
    next = next ?? removePicture(editorState, PICTURE_KEYS[command]);
    if (!next) return 'not-handled';
    this.onEditorStateChange(next);
    return 'handled';
  };

  imageErrorMessage = (reason: string) => {
    // i18next.t, not the i18n() helper: the helper calls changeLanguage, which re-renders every translated
    // component - from render that loops until React gives up and unmounts the app (React error #185).
    const t = i18next.t.bind(i18next);
    switch (reason) {
      case 'type':
        return t('knowledge.add-knowledge-modal.image-wrong-type');
      case 'size':
        return t('knowledge.add-knowledge-modal.image-too-large', { size: MAX_CATALOG_IMAGE_BYTES / 1024 });
      case 'gif-size':
        return t('knowledge.add-knowledge-modal.gif-too-large', { size: MAX_CATALOG_IMAGE_BYTES / 1024 });
      case 'empty':
        return t('knowledge.add-knowledge-modal.image-upload-failed', { reason: t('knowledge.add-knowledge-modal.image-empty') });
      case 'folder':
        return t('knowledge.add-knowledge-modal.image-upload-failed', { reason: t('knowledge.add-knowledge-modal.image-folder-unreachable', { page: t('side-nav.ticket-configuration') }) });
      default:
        return t('knowledge.add-knowledge-modal.image-upload-failed', { reason });
    }
  };

  showImageError = (reason: string) => {
    const message = this.imageErrorMessage(reason);
    if (this.unmounted) return;
    this.setState({ imageError: message });
    // Gone after a few seconds, so a refused file does not leave a warning sitting under the editor for good.
    // A second error restarts the clock rather than being cut short by the first one's timer.
    clearTimeout(this.imageErrorTimer);
    this.imageErrorTimer = setTimeout(() => { if (!this.unmounted) this.setState({ imageError: undefined }); }, IMAGE_ERROR_MS);
  };

  /**
   * The picture goes in where it was pasted straight away, shown from the browser's own copy, and the upload
   * runs behind it. Waiting for the upload first left a gap of seconds with nothing on screen, then dropped the
   * picture wherever the cursor had moved to in the meantime - after whatever had been typed. Until the upload
   * finishes the picture has no marker, so a save in that window leaves it out rather than saving the local URL.
   */
  insertPicture = async (file: Blob, fileName: string) => {
    // Type only: a picture over the size cap is shrunk below, behind the picture already on screen.
    const problem = catalogImageProblem(file.type, 0);
    if (problem) {
      this.showImageError(problem);
      return;
    }

    // Let draft-js finish handling the paste event before the editor state changes under it.
    await new Promise(resolve => setTimeout(resolve, 0));
    if (this.unmounted) return;

    const shownAs = URL.createObjectURL(file);
    // Already being uploaded here, so onEditorStateChange must not take it for a pasted picture and upload it twice.
    this.uploading.add(shownAs);
    const content = this.state.editorState.getCurrentContent().createEntity('IMAGE', 'IMMUTABLE', { src: shownAs, url: shownAs, alt: '', alignment: DEFAULT_IMAGE_ALIGNMENT });
    const withEntity = EditorState.set(this.state.editorState, { currentContent: content });
    // Leaves any error alone: dropping two pictures and a Word document refuses the document and then starts
    // these uploads, and clearing here wiped that message before it was ever seen. Errors clear on a timer.
    this.setState(prev => ({ uploadsInFlight: (prev.uploadsInFlight ?? 0) + 1 }));
    this.onEditorStateChange(AtomicBlockUtils.insertAtomicBlock(withEntity, content.getLastCreatedEntityKey(), ' '));

    try {
      const fitted = await prepareCatalogImage(file);
      if (typeof fitted === 'string') throw new Error(fitted);
      const { marker } = await uploadCatalogImage(fitted, fileNameForType(fileName, fitted.type), shownAs);
      this.reportUpload(marker);
      this.markerBySrc.set(shownAs, marker);
      if (!this.unmounted) this.onEditorStateChange(this.state.editorState);
    } catch (e: unknown) {
      if (!this.unmounted) {
        this.removePicture(shownAs);
        this.showImageError(errorMessage(e));
      }
    } finally {
      this.uploading.delete(shownAs);
      if (!this.unmounted) this.setState(prev => ({ uploadsInFlight: Math.max(0, (prev.uploadsInFlight ?? 1) - 1) }));
    }
  };

  /** Takes out a picture whose upload failed, so what is on screen matches what will be saved. */
  removePicture = (src: string) => {
    const raw: RawData = convertToRaw(this.state.editorState.getCurrentContent());
    const keys = Object.keys(raw.entityMap).filter(k => raw.entityMap[k].type === 'IMAGE' && raw.entityMap[k].data?.src === src);
    if (keys.length === 0) return;
    raw.blocks = raw.blocks.filter(b => !(b.type === 'atomic' && b.entityRanges.some(r => keys.includes(String(r.key)))));
    if (raw.blocks.length === 0) raw.blocks = [{ key: 'empty', text: '', type: 'unstyled', depth: 0, inlineStyleRanges: [], entityRanges: [], data: {} }];
    this.onEditorStateChange(EditorState.push(this.state.editorState, convertFromRaw(raw), 'remove-range'));
  };

  renderPictureBlock = (block: ContentBlock, config: PictureBlockProps) => {
    if (block.getType() !== 'atomic') return undefined;
    const key = block.getEntityAt(0);
    if (!key || config.getEditorState().getCurrentContent().getEntity(key).getType() !== 'IMAGE') return undefined;
    return {
      component: ResizableImage,
      editable: false,
      props: { onChange: config.onChange, getEditorState: config.getEditorState, isReadOnly: config.isReadOnly }
    };
  };

  handlePictureFiles = (files: Blob[]) => {
    if (!this.props.allowImageUpload) return 'not-handled';
    if (!files?.length) return 'not-handled';
    // A file that is not a picture - a Word document, a PDF - used to be ignored without a word, which reads as
    // the drop not working at all. Refuse it with the same message as a picture of the wrong type.
    const pictures = files.filter(f => f.type?.startsWith('image/'));
    if (pictures.length < files.length) this.showImageError('type');
    pictures.forEach(f => this.insertPicture(f, (f as File).name || 'pasted-image.png'));
    return 'handled';
  };

  /** Where the toolbar ends inside the editor's wrapper - it wraps onto more rows when the editor is narrow. */
  toolbarBottom = () => {
    const toolbar = this.wrapper?.querySelector<HTMLElement>('.rdw-editor-toolbar');
    return toolbar ? toolbar.offsetTop + toolbar.offsetHeight : 0;
  };

  /** The picture pop-up's upload. Its errors are shown inside the pop-up, so the message is thrown, not shown here. */
  uploadFromPopup = async (file: File): Promise<string> => {
    const fitted = await prepareCatalogImage(file);
    if (typeof fitted === 'string') throw new Error(this.imageErrorMessage(fitted));
    try {
      const { marker, displayUrl } = await uploadCatalogImage(fitted, fileNameForType(file.name, fitted.type));
      this.reportUpload(marker);
      this.markerBySrc.set(displayUrl, marker);
      return displayUrl;
    } catch (e: unknown) {
      throw new Error(this.imageErrorMessage(errorMessage(e)));
    }
  };

  /** An upload that finishes after the editor has gone can never be saved, so it is handed straight back. */
  reportUpload = (marker: string) => {
    const id = catalogImageId(marker);
    if (this.unmounted) discardCatalogImages([id]);
    else this.props.onImageUploaded?.(id);
  };

  /** The pop-up's image was cleared or never added, so nothing can refer to it. */
  discardPopupImage = (src: string) => {
    const marker = this.markerBySrc.get(src);
    if (marker) discardCatalogImages([catalogImageId(marker)]);
  };

  renderImagePopup = (props: LibraryPopupProps) => (
    <CatalogImagePopup {...props} upload={this.props.allowImageUpload ? this.uploadFromPopup : undefined} discard={this.discardPopupImage}
      onOpenChange={open => { this.imagePopupOpen = open; }}
      // The library closes the pop-up without giving the answer the keyboard back, so Enter after adding a picture
      // went nowhere until the answer was clicked. Draft-js puts the cursor on the line it left below the picture.
      onAdded={() => setTimeout(() => this.draftEditor?.focus())} />
  );

  /**
   * A picture the library inserts itself (the URL tab, a pasted HTML image) arrives with no alignment.
   * Entity data is shared, so setting it here - before the new block first renders - is what shows it
   * left, and what gets saved.
   */
  alignNewPicturesLeft = (content: ContentState) => {
    content.getBlockMap().forEach(block => block.findEntityRanges(
      character => {
        const key = character.getEntity();
        if (!key) return false;
        const entity = content.getEntity(key);
        return entity.getType() === 'IMAGE' && !entity.getData().alignment;
      },
      start => { content.mergeEntityData(block.getEntityAt(start), { alignment: DEFAULT_IMAGE_ALIGNMENT }); }
    ));
  };

  /**
   * A picture pasted as part of HTML arrives as a data URI, or - copied out of another catalog item's editor - as
   * that editor's `blob:` address; upload it rather than save its bytes, or an address only this page can open,
   * into the answer. A copy, never the other item's picture: each picture belongs to one item, which is what its
   * visibility and its clean-up are decided by. One that is refused or fails is taken out again, as a pasted file
   * is - a `blob:` dies with the page that made it, so one copied before a reload fails here.
   */
  uploadPastedImage = async (src: string) => {
    if (!this.props.allowImageUpload || this.uploading.has(src) || !isLocalImageSrc(src)) return;
    this.uploading.add(src);
    this.setState(prev => ({ uploadsInFlight: (prev.uploadsInFlight ?? 0) + 1 }));
    try {
      const picture = await this.pastedImageBytes(src);
      const fitted = await prepareCatalogImage(picture);
      if (typeof fitted === 'string') throw new Error(fitted);
      const { marker } = await uploadCatalogImage(fitted, fileNameForType('pasted-image.png', fitted.type));
      this.reportUpload(marker);
      this.markerBySrc.set(src, marker);
      if (!this.unmounted) this.onEditorStateChange(this.state.editorState);
    } catch (e: unknown) {
      if (!this.unmounted) {
        this.removePicture(src);
        this.showImageError(errorMessage(e));
      }
    } finally {
      this.uploading.delete(src);
      if (!this.unmounted) this.setState(prev => ({ uploadsInFlight: Math.max(0, (prev.uploadsInFlight ?? 1) - 1) }));
    }
  };

  pastedImageBytes = async (src: string): Promise<Blob> => {
    if (/^blob:/i.test(src)) return (await fetch(src)).blob();
    const parsed = parseDataUrl(src);
    if (!parsed) throw new Error('type');
    return new Blob([parsed.bytes], { type: parsed.type });
  };

  /**
   * Whether a pasted picture can be kept: an address on the web always can, and so can one of this editor's own
   * pictures copied within it. A `data:` or `blob:` picture only where it can be uploaded - see uploadPastedImage.
   */
  canKeepPastedPicture = (src: string) =>
    !isLocalImageSrc(src) || this.markerBySrc.has(src) || !!this.props.allowImageUpload;

  /**
   * Pasted HTML with pictures in it - from another catalog item, or a web page - is inserted here instead of by
   * draft-js, so its pictures arrive inside lines and are lifted, checked and uploaded like any other (see
   * picturesAsInline for what went wrong before). So is any copy from another catalog item, pictures or not, so it
   * keeps its empty lines (holdEmptyLines), and so is anything carrying a stylesheet - a copy from Word - so the
   * stylesheet is not pasted as text (withoutPastedMarkup). A copy within this editor is left to draft-js, which pastes it from its
   * own clipboard with each picture's marker intact.
   */
  // Text pasted while the picture pop-up is open belongs to its Link box, never the answer: an address pasted while
  // the answer still had focus went in as a line of text above the picture.
  imagePopupOpen = false;

  handlePastedHtml = (_text: string, html: string, editorState: EditorState, setEditorState: (editorState: EditorState) => void) => {
    if (this.imagePopupOpen) return true;
    if (!html || !(/<img\b/i.test(html) || isDraftEditorHtml(html) || hasPastedStyles(html))) return false;
    const key = this.draftEditor?.getEditorKey?.();
    if (key && html.includes(`data-editor="${key}"`)) return false;
    const pasted = pasteHtmlWithPictures(editorState, html, this.draftEditor?.props?.blockRenderMap);
    if (!pasted) return false;
    setEditorState(pasted);
    return true;
  };

  onEditorStateChange: Function = editorState => {
    // Not a new undo step: one Ctrl+Z still takes the whole paste back, rather than back to the camera emojis,
    // which would only be lifted again.
    editorState = withPicturesLifted(editorState, this.canKeepPastedPicture);

    this.alignNewPicturesLeft(editorState.getCurrentContent());
    // Entity data is shared with the editor state, so the marker set here is what the editor holds from now on.
    Object.values((convertToRaw(editorState.getCurrentContent()) as RawData).entityMap).forEach(e => {
      if (e.type !== 'IMAGE' || e.data?.marker) return;
      if (this.markerBySrc.has(e.data?.src)) e.data.marker = this.markerBySrc.get(e.data.src);
      else if (isLocalImageSrc(e.data?.src)) this.uploadPastedImage(e.data.src);
    });

    // Only a change to what would be saved is reported - see changeToReport.
    const md = this.toMarkdown(editorState);
    const report = changeToReport(md, { loaded: this.loadedMarkdown, loadedAsSaved: this.loadedAsSaved, lastReported: this.lastReported });
    if (report !== null) {
      this.lastReported = md;
      if (this.props.onChange) this.props.onChange(report);
    }

    this.setState({ editorState });
  };

  /** The Markdown this editor saves for a given state. */
  toMarkdown = (editorState: EditorState): string => {
    return answerToMarkdown(convertToRaw(editorState.getCurrentContent()), !!this.props.keepBlankLines);
  };

  onHandleBeforeInput: Function = val => {
    const textLength = this.state.editorState.getCurrentContent().getPlainText().length;
    return (val && textLength >= this.maxLength) ? 'handled' : 'not-handled';
  };

  setEditorReference: Function = ref => {
    this.draftEditor = ref;
    if (this.props.isFocus) {
      ref?.focus();
    }
  };

  render() {
    const { editorState } = this.state;
    let positionClass = this.props.position === 'bottom' ? `b-no-space` : '';
    let hasImageClass = this.props.noImage ? `no-image` : '';
    const border = this.props.useBorder ? { border: '1px solid var(--mgt-theme-border-color)' } : {};
    const height = this.state.fitHeight || this.props.height;
    const editorStyle = height ? { height } : {};

    return (
      <AppStateContext.Consumer>
        {(state) => {
          return (
            <ProviderConsumer render={(globalTheme) => (
              <div ref={el => { this.wrapper = el; }} className={`tikit-editor ${positionClass} ${hasImageClass} ${state.theme === themeNames.Dark ? 'editor--darkmode' : 'editor--light'} ${this.props.keepBlankLines ? 'editor--lines-as-typed' : ''}`}
                style={{ position: 'relative', backgroundColor: 'var(--mgt-theme-background)', ...border, ...ThemeColorScheme(globalTheme.siteVariables) }}
              >
                <Editor
                  readOnly={this.props.readOnly}
                  editorRef={this.setEditorReference}
                  editorState={editorState}
                  spellCheck={true}
                  wrapperClassName="demo-wrapper"
                  editorClassName="demo-editor editor-scroll-to-bottom"
                  editorStyle={editorStyle} 
                  onEditorStateChange={this.onEditorStateChange}
                  handleBeforeInput={this.onHandleBeforeInput}
                  handlePastedText={this.handlePastedHtml}
                  handlePastedFiles={this.handlePictureFiles}
                  handleDroppedFiles={(_selection, files) => this.handlePictureFiles(files)}
                  customBlockRenderFunc={this.renderPictureBlock}
                  handleKeyCommand={this.handleKeyCommand}
                  blockStyleFn={() => 'defaultStyle'}
                  inline
                  localization={{ locale: 'en', translations: editorLabels() }}
                  toolbar={{
                    options: [
                      'inline',
                      'blockType',
                      'list',
                      'link',
                      'emoji',
                      'image',
                      'remove',
                      'history'
                    ],
                    inline: {
                      inDropdown: false,
                      className: undefined,
                      component: undefined,
                      dropdownClassName: undefined,
                      options: ['bold', 'italic', 'strikethrough'],
                      ...inlineIcons
                    },
                    ...controlIcons(),
                    // A Teams card has three text sizes, and the web shows H2-H6 as plain text, so H3-H6 could
                    // be picked but would look no different anywhere the answer is read.
                    blockType: {
                      inDropdown: true,
                      options: ['Normal', 'H1', 'H2', 'Blockquote', 'Code']
                    },
                    image: {
                      icon: toolbarIcons.image,
                      component: this.renderImagePopup
                    }
                  }}
                />
                {(!!this.state.uploadsInFlight || !!this.state.imageError) && (
                  // Floating just under the toolbar, where the eye already is, rather than under a tall editor where it
                  // could be off screen. Over the text, not in the layout, so the editor does not jump as one comes and goes.
                  <div className="catalog-editor-messages" style={{ top: this.toolbarBottom() }}>
                    {!!this.state.uploadsInFlight && (
                      <div role="status" aria-live="polite" className="catalog-editor-message">
                        <Loader size="smallest" inline />
                        <span>{i18next.t('knowledge.add-knowledge-modal.image-uploading', { count: this.state.uploadsInFlight })}</span>
                      </div>
                    )}
                    {this.state.imageError && (
                      <div role="alert" className="catalog-editor-message catalog-editor-message--error">
                        <ExclamationCircleIcon size="small" className="catalog-editor-message-icon" />
                        <span>{this.state.imageError}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )} />
          )
        }}
      </AppStateContext.Consumer>
    );
  }
}

export class HtmlEditorComponent extends React.Component<HtmlProps, State> {
  toolbarOptions: string[];
  toolbarInline: any;
  // The catalog's picture pop-up, link only: the library's inserted any address, so a bad one went in as a broken
  // picture. A field, not inline, or every render would remount the pop-up and lose what was typed.
  renderImagePopup = (props: LibraryPopupProps) => (
    <CatalogImagePopup {...props} onChange={(src, height, width, alt) => {
      this.addingPicture = true;
      props.onChange(src, height, width, alt);
    }} />
  );
  // The library inserts a picture with no alignment, which it draws centred while the saved HTML leaves it on the
  // left. Set to left as it goes in, so the editor shows where it will be sent. The library's insert reaches
  // onEditorStateChange synchronously, so the flag covers exactly that one change.
  addingPicture = false;

  // The opening text as this editor writes it, so the comparison is like for like.
  loadedHtml: string;
  contentEdited = false;
  constructor(props: any) {
    super(props);
    const contentState = editorContent(this.props.htmlString);
    const newEditorState = EditorState.createWithContent(contentState);
    this.loadedHtml = editorHtml(convertToRaw(contentState));
    this.state = { editorState: newEditorState };
    if (this.props.toolbarOptions)
      this.toolbarOptions = this.props.toolbarOptions;
    if (this.props.toolbarInline)
      this.toolbarInline = this.props.toolbarInline;
  }

  onEditorStateChange: Function = (editorState: EditorState) => {
    if (this.addingPicture) {
      this.addingPicture = false;
      const content = editorState.getCurrentContent();
      const key = content.getLastCreatedEntityKey();
      if (key && content.getEntity(key).getType() === 'IMAGE' && !content.getEntity(key).getData().alignment)
        content.mergeEntityData(key, { alignment: 'left' });
    }
    const rawdata = convertToRaw(editorState.getCurrentContent());
    const html = editorHtml(rawdata);
    this.contentEdited = this.contentEdited || html !== this.loadedHtml;
    if (this.props.onChange) this.props.onChange(html, this.contentEdited);
    this.setState({ editorState });
  };

  componentDidUpdate(prevProps: HtmlProps) {
    if (prevProps.linesAsTyped === false && this.props.linesAsTyped)
      this.onEditorStateChange(withOldGapsAsEmptyLines(this.state.editorState));
  }

  handlePastedText: Function = (text) => {
    //This will highlight the token after pasting
    setTimeout(()=>{
      const regex = /\{{1,2}([^{}]+?)\}{1,2}/;
      let pastedTokens = document.getElementsByClassName("rdw-link-decorator-wrapper");
      for(const tokenContainer of pastedTokens){
        if (regex.test(tokenContainer.outerHTML)){
          let link = tokenContainer.getElementsByTagName("a");
          let token = link[0];
          token.className="rdw-mention-link";
        }
      }
    },100);

    return false;
  }

  /**
   * Draft-js key commands. Passing one replaces react-draft-wysiwyg's own, which applies the toolbar's inline styles
   * from the keyboard, so that is done here too, with the same options.
   */
  handleKeyCommand = (command: string, editorState: EditorState) => {
    const inlineOptions: string[] = this.toolbarInline?.options ?? ['bold', 'italic', 'strikethrough'];
    const next = inlineOptions.includes(command) ? RichUtils.handleKeyCommand(editorState, command) : removePicture(editorState, command);
    if (!next) return 'not-handled';
    this.onEditorStateChange(next);
    return 'handled';
  };

  wrapper = React.createRef<HTMLDivElement>();

  componentDidMount() {
    this.wrapper.current?.addEventListener('mouseup', this.selectClickedPicture);
  }

  componentWillUnmount() {
    this.wrapper.current?.removeEventListener('mouseup', this.selectClickedPicture);
  }

  // A picture's figure is keyed "<block key>-<decorator>-<leaf>", as draft-js renders every block. Set on the next
  // tick: draft-js takes the browser's selection as the click lands, which is still wherever the caret was. On
  // mouseup, not click: the first click focuses the editor, draft-js re-renders the picture under the pointer, and the
  // browser then fires no click at all.
  selectClickedPicture = (event: MouseEvent) => {
    if (this.props.readOnly || !(event.target instanceof HTMLImageElement)) return;
    const blockKey = event.target.closest('[data-offset-key]')?.getAttribute('data-offset-key')?.split('-')[0];
    if (!blockKey) return;
    setTimeout(() => {
      const selected = selectPicture(this.state.editorState, blockKey);
      if (selected) this.setState({ editorState: selected });
    });
  };

  render() {
    const { editorState } = this.state;
    return (
      <ProviderConsumer
        render={globalTheme => (
          <div className={`tikit-editor ${globalTheme.siteVariables.theme !== themeNames.Default ? 'dark-mood-custom editor--darkmode' : 'editor--light'} mt-2`}
            style={{ position: 'relative', color: this.props.readOnly ? 'var(--mgt-theme-foreground-disabled)' : 'var(--mgt-theme-foreground)', backgroundColor: this.props.readOnly ? 'var(--mgt-theme-background-disabled)' : 'var(--mgt-theme-background)', ...ThemeColorScheme(globalTheme.siteVariables) }}
            ref={this.wrapper}
          >
            <Editor
              editorState={editorState}
              wrapperClassName={`${this.props.wrapperClassName ?? 'editor-wrapper'} ${this.props.readOnly ? ' editor-wrapper--disabled' : ''}`}
              editorClassName={this.props.editorClassName ?? 'editor-default'}
              toolbarClassName={`editor-toolbar`}
              onEditorStateChange={this.onEditorStateChange}
              blockStyleFn={() => 'defaultStyle'}
              mention={this.props.mention}
              handleReturn={() => {
                return this.props.oneLine;
              }}
              handlePastedText={this.handlePastedText}
              handleKeyCommand={this.handleKeyCommand}
              localization={{ locale: 'en', translations: editorLabels() }}
              toolbar={{
                ...controlIcons(),
                options: this.toolbarOptions ?? [
                  'inline',
                  'blockType',
                  'list',
                  'link',
                  'emoji',
                  'image',
                  'remove',
                  'history'
                ],
                inline: {
                  ...(this.toolbarInline ?? {
                    inDropdown: false,
                    className: undefined,
                    component: undefined,
                    dropdownClassName: undefined,
                    options: ['bold', 'italic', 'strikethrough']
                  }),
                  ...inlineIcons
                },
                image: { icon: toolbarIcons.image, component: this.renderImagePopup }
              }}
              readOnly={this.props.readOnly}
              spellCheck={this.props.spellCheck}
            />
          </div>
        )}
      />
    );
  }
}